import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, fmtDate, formatApiError, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { usePager, Pager } from "@/components/Pager";
import { Plus, Trash2, Wallet, CalendarCheck, Building2, FileDown, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const MODES = ["NEFT", "RTGS", "IMPS", "UPI", "Cheque", "Cash", "Bank Transfer"];
const today = () => new Date().toISOString().slice(0, 10);

export const VendorPayables = () => {
  const [ov, setOv] = useState(null);
  const [payments, setPayments] = useState([]);
  const payPager = usePager(payments);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ vendor_id: "", amount: "", date: today(), mode: "NEFT", reference: "", notes: "" });
  const [vbills, setVbills] = useState([]);
  const [selBills, setSelBills] = useState({});
  const [billFilter, setBillFilter] = useState("");

  useEffect(() => {
    setSelBills({});
    setVbills([]);
    setBillFilter("");
    if (open && form.vendor_id) {
      api.get(`/vendors/${form.vendor_id}/bills-detail`)
        .then((r) => setVbills((r.data.bills || []).filter((b) => b.number && b.due > 0.01)))
        .catch(() => {});
    }
  }, [form.vendor_id, open]);

  const toggleBill = (b) => {
    const n = { ...selBills, [b.number]: !selBills[b.number] };
    setSelBills(n);
    const sum = vbills.filter((x) => n[x.number]).reduce((t, x) => t + (x.due || 0), 0);
    setForm((f) => ({ ...f, amount: sum > 0 ? String(+sum.toFixed(2)) : "" }));
  };
  const selectedBills = vbills.filter((b) => selBills[b.number]);

  const load = () => Promise.all([
    api.get("/vendors/payables/overview").then((r) => setOv(r.data)),
    api.get("/vendors/payments/all").then((r) => setPayments(r.data)),
  ]).catch(() => {});
  useEffect(() => { load(); }, []);

  const openPay = (vendorId = "") => {
    setForm({ vendor_id: vendorId, amount: "", date: today(), mode: "NEFT", reference: "", notes: "" });
    setOpen(true);
  };

  const selVendor = ov?.vendors?.find((v) => v.vendor_id === form.vendor_id);

  const save = async () => {
    if (!form.vendor_id) { toast.error("Select a vendor"); return; }
    if (!(+form.amount > 0)) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/vendors/${form.vendor_id}/payments`, {
        amount: +form.amount, date: form.date, mode: form.mode, reference: form.reference, notes: form.notes,
        bill_numbers: selectedBills.map((b) => b.number),
      });
      const allocs = r.data?.allocations || [];
      const cleared = allocs.filter((a) => a.cleared).length;
      toast.success(selectedBills.length
        ? `Payment recorded against ${selectedBills.length} selected bill${selectedBills.length === 1 ? "" : "s"}`
        : allocs.length
          ? `Payment recorded — settled ${allocs.length} bill${allocs.length === 1 ? "" : "s"} oldest-first (${cleared} fully cleared)`
          : "Payment recorded");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const del = async (p) => {
    if (!window.confirm(`Delete this payment of ${inr(p.amount)} to ${p.vendor_name}? The vendor's payable balance will be restored.`)) return;
    try {
      await api.delete(`/vendors/payments/${p.id}`);
      toast.success("Payment deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cards = [
    { label: "Total Payable", value: ov?.total_payable, icon: Wallet, color: "#F43F5E", hint: "owed to vendors" },
    { label: "Paid This Month", value: ov?.paid_this_month, icon: CalendarCheck, color: "#10B981", hint: "current month" },
    { label: "Vendors With Dues", value: ov?.vendors_with_dues, icon: Building2, color: "#F97316", hint: "open balances", plain: true },
  ];

  if (open) {
    return (
      <div className="space-y-4" data-testid="payable-record-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-semibold text-slate-900">Record Vendor Payment</h1>
            <p className="text-sm text-slate-500">Tick bills to auto-fill the amount — payment applies to selected bills, oldest first</p>
          </div>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="payable-back">
            <ArrowLeft size={14} className="mr-1" /> Back to Payables
          </Button>
        </div>

        <div className="border border-slate-100 rounded-xl bg-white p-5 shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
          <div className="grid lg:grid-cols-2 gap-x-8 gap-y-4">
            <div className="flex flex-col gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Vendor *</Label>
                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="payable-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {(ov?.vendors || []).map((v) => (
                      <SelectItem key={v.vendor_id} value={v.vendor_id}>
                        {v.name} <span className="text-slate-500">· payable {inr(v.payable)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selVendor && (
                  <p className="text-[11px] mt-1.5 text-slate-500">
                    Outstanding payable: <b className={selVendor.payable > 0.01 ? "text-rose-600" : "text-emerald-700"}>{inr(selVendor.payable)}</b>
                    {selVendor.payable > 0.01 && (
                      <button className="ml-2 text-blue-600 hover:underline" onClick={() => setForm({ ...form, amount: selVendor.payable })} data-testid="payable-fill-full">pay full</button>
                    )}
                  </p>
                )}
              </div>
              {form.vendor_id && vbills.length > 0 && (
                <div className="flex flex-col">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Against Bills (optional — tick to add up)</Label>
                  {vbills.length > 5 && (
                    <Input value={billFilter} onChange={(e) => setBillFilter(e.target.value)} placeholder="Search bill number…"
                           className="mt-1.5 h-9" data-testid="payable-bill-search" />
                  )}
                  <div className="mt-1.5 max-h-[55vh] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="payable-bill-list">
                    {vbills.filter((b) => !billFilter || String(b.number).toLowerCase().includes(billFilter.toLowerCase())).map((b) => (
                      <div key={b.id} onClick={() => toggleBill(b)} className="flex items-start gap-2.5 px-2.5 py-2 cursor-pointer hover:bg-slate-50" data-testid={`payable-bill-row-${b.number}`}>
                        <Checkbox checked={!!selBills[b.number]} className="mt-0.5 pointer-events-none shrink-0" data-testid={`payable-bill-check-${b.number}`} />
                        <div className="flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="font-mono text-[13px] font-semibold break-all" style={{ color: "#0f172a" }}>{String(b.number || "—")}</div>
                            <div className="tabular-nums text-rose-600 font-semibold text-xs whitespace-nowrap">due {inr(b.due)}</div>
                          </div>
                          <div className="text-[11px] text-slate-500">{fmtDate(b.date)} · bill {inr(b.amount)}</div>
                        </div>
                      </div>
                    ))}
                    {billFilter && vbills.every((b) => !String(b.number).toLowerCase().includes(billFilter.toLowerCase())) && (
                      <p className="px-2.5 py-2 text-xs text-slate-400">No bill matches "{billFilter}"</p>
                    )}
                  </div>
                  {selectedBills.length > 0 && (
                    <p className="text-[11px] text-emerald-700 mt-1.5 font-medium" data-testid="payable-bill-sum">
                      {selectedBills.length} bill{selectedBills.length === 1 ? "" : "s"} selected — amount set to their total due {inr(selectedBills.reduce((t, b) => t + b.due, 0))}
                    </p>
                  )}
                </div>
              )}
              {form.vendor_id && vbills.length === 0 && (
                <p className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-md px-3 py-4">No unpaid bills for this vendor — the payment will stay on account.</p>
              )}
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Amount *</Label>
                  <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-11" data-testid="payable-amount" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11" data-testid="payable-date" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Mode</Label>
                  <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="payable-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">UTR / Reference</Label>
                  <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5 h-11" data-testid="payable-reference" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="payable-notes" />
              </div>
              <p className="text-[11px] text-slate-500">
                {selectedBills.length
                  ? "The payment is applied to the selected bills, oldest first."
                  : "No bills selected — the payment auto-settles this vendor's oldest unpaid bills first; any excess stays on account."}
              </p>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="payable-cancel">Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="payable-save">{saving ? "Saving…" : "Record Payment"}</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="payables-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Payables</h1>
          <p className="text-sm text-slate-500">Vendor payments — payments settle the oldest bills first automatically</p>
        </div>
        <Button onClick={() => openPay()} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="payable-new">
          <Plus size={14} className="mr-1" /> Record Vendor Payment
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="border border-slate-100 rounded-xl bg-white p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)] flex items-center gap-3" data-testid={`payable-card-${c.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-none" style={{ background: c.color }}>
              <c.icon size={18} />
            </div>
            <div>
              <div className="text-xl font-semibold text-slate-900 tabular-nums">{c.plain ? (c.value ?? "—") : inr(c.value || 0)}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{c.label} · {c.hint}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <div className="px-4 py-3 text-sm font-semibold text-slate-800 border-b border-slate-100">Vendor Balances</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60">
                <th className="px-4 py-2.5">Vendor</th>
                <th className="px-4 py-2.5 text-right">Billed</th>
                <th className="px-4 py-2.5 text-right">Paid</th>
                <th className="px-4 py-2.5 text-right">Payable</th>
                <th className="px-4 py-2.5 text-center">Bills</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(ov?.vendors || []).map((v) => (
                <tr key={v.vendor_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`payable-vendor-${v.vendor_id}`}>
                  <td className="px-4 py-2.5 font-medium">
                    <Link to={`/payments/payables/vendor/${v.vendor_id}`} className="text-slate-800 hover:text-blue-600 hover:underline" data-testid={`payable-vendor-link-${v.vendor_id}`}>{v.name}</Link>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{inr(v.billed)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{inr(v.paid)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${v.payable > 0.01 ? "text-rose-600" : "text-slate-500"}`}>{inr(v.payable)}</td>
                  <td className="px-4 py-2.5 text-center text-slate-600">{v.bills}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <a href={`${API_URL}/vendors/${v.vendor_id}/ledger/pdf`} target="_blank" rel="noreferrer"
                         className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-400" title="Download statement PDF" data-testid={`payable-stmt-${v.vendor_id}`}>
                        <FileDown size={13} />
                      </a>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openPay(v.vendor_id)} data-testid={`payable-pay-${v.vendor_id}`}>Pay</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {ov && ov.vendors.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-500">No vendor bills or payments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <div className="px-4 py-3 text-sm font-semibold text-slate-800 border-b border-slate-100">Payment History</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60">
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Vendor</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5">Mode · Reference</th>
                <th className="px-4 py-2.5">Settled Against</th>
                <th className="px-4 py-2.5 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {payPager.pageItems.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`vendor-payment-row-${p.id}`}>
                  <td className="px-4 py-2.5 text-slate-600">{fmtDate(p.date)}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{p.vendor_name}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{inr(p.amount)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{p.mode}{p.reference ? ` · ${p.reference}` : ""}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">
                    {(p.allocations || []).length
                      ? p.allocations.map((a) => `${a.bill_number || "bill"}${a.cleared ? " ✓" : " (part)"}`).join(", ")
                      : (p.bill_numbers || []).join(", ") || (p.po_number || "On account")}
                    {p.unallocated > 0.01 ? ` · ${inr(p.unallocated)} on account` : ""}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => del(p)} className="p-1.5 rounded-md text-slate-400 hover:text-rose-600" title="Delete (admin)" data-testid={`vendor-payment-del-${p.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-500">No vendor payments recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager pager={payPager} />
      </div>
    </div>
  );
};

